import moment from "moment";
import React, { useState } from "react";
import { Container, ListGroup } from "react-bootstrap";
import { ICON } from "../../assets";
import { navigate } from "../../routes";
import { admin } from "../../routes/admin";
import sdk from "../../sdk/sdk";
import { formatDateToFrench, price } from "../../utils";
import { useAuth } from "../Auth/useAuth";
import useTranslations from "../Translations/useTranslations";
import UiBadge, { UI_BADGE_TYPE } from "../ui/UiBadge";
import UiButton, { UI_BUTTON_TYPE } from "../ui/UiButton";
import UiIcon from "../ui/UiIcon";
import "./CartsList.css";
import { useCart } from "./useCart";
import { types } from "../Auth/reducer";
import { useDispatch } from "react-redux";
import { registerController } from "../../requestRegistry";

function CartsList({ closeList }) {
  const [{ user }] = useAuth();
  const clientProfile = user && user.client_profile;
  const trl = useTranslations();
  const [
    carts,
    {
      removeCart,
      selectCart,
      getCartDataByID,
      removeAllCart,
      replaceCartId,
      flushSyncCartToDB,
    },
  ] = useCart(user.client_profile?.id);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  function renderCart({ cart }) {
    const _deleteCartById = async (cart) => {
      if (!window.confirm(trl.message("HomePage.Dialog.deleteCart"))) return;
      const codcli = cart.codcli ?? cart.client?.CODCLI ?? clientProfile?.id;
      setLoading(true);
      try {
        await removeCart({ creator: codcli, _id: cart._id });
      } finally {
        setLoading(false);
      }
    };

    const _navigateToCartDetail = (_id) => {
      const cart = carts.carts.find((c) => c._id === _id);

      // `codcli` may be missing on carts migrated from old localStorage.
      // Fall back to the embedded client object, then to the logged-in profile.
      const codcli =
        cart.codcli ??
        cart.client?.CODCLI ??
        clientProfile?.id;

      dispatch({
        type: types.ADD_CLIENT_PROFILE,
        payload: {
          id: codcli,
          email: cart.client.EMAIL,
          name: cart.client.NOMCLI,
          tel: cart.client.TEL,
        },
      });
      dispatch({
        type: types.DELETE_LOYALTY_REWARD,
      });

      // Flush any pending cart saves before navigation
      if (flushSyncCartToDB) {
        flushSyncCartToDB();
      }

      setLoading(true);
      const controller = new AbortController();
      const unregister = registerController(controller);
      sdk.orders
        .checkAndSavecartbyIdDB(codcli, _id, cart, controller.signal)
        .then((data) => {
          unregister();
          if (data.payload.header.id != _id) {
            replaceCartId(_id, data.payload.header.id, codcli);
            _id = data.payload.header.id;
          }
          selectCart({ creator: codcli, _id });
          navigate.push(admin.APP_NAVIGATION, { critere: "start" });
        })
        .catch((err) => {
          unregister();
          // Silent: user clicked "Revenir à mon compte" and aborted the request
          if (err?.name === "AbortError") return;
          alert(
            "Erreur lors de l'ouverture du panier. Vous pouvez réessayer ou supprimer et créer un nouveau panier.",
          );
        })
        .finally(() => setLoading(false));
    };

    const getCartpriceByID = (cartId) => {
      if (!cartId) return 0;
      const cart = carts.carts.find((c) => c._id === cartId);
      if (!cart) return 0;
      const { mt_total, mt_total_remise_pack, cumul_plv_in_cart } =
        getCartDataByID(cart._id);
      return price(mt_total - mt_total_remise_pack - cumul_plv_in_cart) + " €";
    };

    return (
      <React.Fragment>
        <ListGroup.Item key={cart._id} className="cart-list-item">
          <div className="cart-info">
            <div className="cart-list-item__header">
              <div>
                <div>{formatDateToFrench(new Date(cart.created_at))}</div>
                {!clientProfile ? <div>{cart.client.NOMCLI}</div> : ""}
              </div>
              <UiButton
                disabled={loading}
                classNames={["cart-list-item-button"]}
                onClick={() => _navigateToCartDetail(cart._id)}
                content={`Reprendre`}
              />
            </div>
            <div className="cart-list-item__footer">
              <div>{getCartpriceByID(cart._id)}</div>
              <UiIcon
                disabled={loading}
                icon={ICON.trashIcon}
                onClick={() => !loading && _deleteCartById(cart)}
                alt="Supprimer le panier"
                size="lg"
              />
            </div>
          </div>
        </ListGroup.Item>
        <span className="cart-list-item-separator">&nbsp;</span>
      </React.Fragment>
    );
  }

  function renderCarts() {
    const getItems = carts?.carts?.sort((item1, item2) => {
      const item1Date = moment(item1.created_at);
      const item2Date = moment(item2.created_at);
      // Order by datetime desc
      if (item1Date.isAfter(item2Date)) return -1;
      if (item2Date.isAfter(item1Date)) return 1;
      return 0;
    });
    if (!getItems || getItems.length === 0) {
      return (
        <React.Fragment>
          <ListGroup variant="flush" className="cart-list ">
            <ListGroup.Item style={{ textAlign: "center", margin: "20px 0" }}>
              Aucun panier disponible
            </ListGroup.Item>
          </ListGroup>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <ListGroup variant="flush" className="cart-list">
          {getItems.map((cart) => (
            <div key={cart._id}> {renderCart({ cart })}</div>
          ))}
        </ListGroup>
      </React.Fragment>
    );
  }

  function renderCartHeader() {
    const cartCount = carts.carts.length;
    return (
      <React.Fragment>
        <Container fluid className="cart-header">
          <div className="cart-header-left">
            <UiBadge content={cartCount} badgeUiType={[UI_BADGE_TYPE.MEDIUM]} />
            <span>{cartCount === 0 ? "panier" : "paniers"} en attente</span>
          </div>
          <UiIcon
            disabled={loading}
            icon={ICON.closeCrossIcon}
            className="cart-header-close"
            onClick={() => !loading && closeList(false)}
            size="lg"
          />
        </Container>
      </React.Fragment>
    );
  }

  function renderCartFooter() {
    const _deleteAllCarts = async () => {
      if (!window.confirm("Do you really want to delete all baskets?")) return;
      setLoading(true);
      try {
        await removeAllCart({ creator: user.client_profile?.id });
      } finally {
        setLoading(false);
      }
    };
    if (carts?.carts?.length <= 1) return null;

    return (
      <React.Fragment>
        <Container className="cart-footer">
          <UiButton
            disabled={loading}
            classNames={["cart-lists-delete-button"]}
            buttonUiType={[UI_BUTTON_TYPE.ONLY_BORDER]}
            onClick={_deleteAllCarts}
            content="Supprimer tous les paniers"
          />
        </Container>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="cart-container">
        {renderCartHeader()}
        {renderCarts()}
        {clientProfile?.id && renderCartFooter()}
      </div>
    </React.Fragment>
  );
}

export default CartsList;
